/**
 * Filter Registry - Single source of truth for all Knowledge Base filters
 *
 * This registry defines:
 * - UI component and props
 * - GraphQL query mapping
 * - Display labels and formatting
 * - Data loading requirements
 *
 * To add a new filter, just add one entry here - no need to touch multiple files.
 */

import { DocumentNode } from "@apollo/client";
import { round } from "lodash";
import * as types from "_graphql-types/graphql";
import type { AntdTreeNode as DataNode } from "frontend/src/components/Tree/treeData";
import { ENTITY_COLORS } from "../constants";
import type { FilterValue, ActiveFilters } from "../stores/knowledgeBaseStore";
import type { EntityFilterProps } from "../Filters/EntityFilter.component";
import type { DateRangeFilterProps } from "../Filters/DateRangeFilter.component";
import type { LocationFilterProps } from "../Filters/LocationFilter.component";
import type { SizeFilterProps } from "../Filters/SizeFilter.component";
import type { PerformanceFilterProps } from "../Filters/PerformanceFilter.component";
import { getPerformanceUnit } from "../Filters/performanceUnit";
import { getPerformanceStatLabel } from "Helpers/performanceStatOptions";
import type {
  VintageYearFilterProps,
  YearFoundedFilterProps,
} from "../Filters/YearFilter.component";
import type {
  MultiSelectFilterData,
  MultiSelectTreeFilterData,
} from "../UnifiedFilters.component";
import {
  ASSET_CLASS_QUERY,
  THEME_OPTIONS_QUERY,
  GEOGRAPHY_OPTIONS_QUERY,
  INVESTMENT_TYPE_OPTIONS_QUERY,
  DOCUMENT_TYPE_ENUM_QUERY,
  NOTE_META_ENUM_QUERY,
  KNOWLEDGE_BASE_TAGS_QUERY,
  INDUSTRY_OPTIONS_QUERY,
  INVESTMENT_TAGS_OPTIONS_QUERY,
  buildAssetClassFilter,
  buildThemeFilter,
  buildGeographyFilter,
  buildInvestmentTypeFilter,
  buildDocumentTypeFilter,
  buildDocumentFormatFilter,
  buildNoteMetaFilter,
  buildKnowledgeBaseTagsFilter,
  buildIndustryFilter,
  buildInvestmentTagsFilter,
} from "../filterDefinitions";

// Import the existing FilterDefinition type but rename it to avoid conflict
import type { FilterDefinition as ExternalFilterDefinition } from "../filterDefinitions";

// ============================================================================
// Types
// ============================================================================

export type FilterCategory =
  | "Knowledge Base"
  | "Note"
  | "Document"
  | "Investment"
  | "Firm"
  | "Company";

export type FilterComponentType =
  | "multiSelect"
  | "entity"
  | "dateRange"
  | "location"
  | "size"
  | "performance"
  | "vintageYear"
  | "yearFounded"
  | "tree";

// Component prop types - re-exported from actual components and UnifiedFilters
export type { EntityFilterProps };
export type { DateRangeFilterProps };
export type { LocationFilterProps };
export type { SizeFilterProps };
export type { PerformanceFilterProps };
export type { VintageYearFilterProps, YearFoundedFilterProps };
export type { MultiSelectFilterData as MultiSelectComponentProps };
export type { MultiSelectTreeFilterData as TreeComponentProps };

// Discriminated union for component props based on component type
// Fixed to match actual component interfaces
export type FilterComponentProps<T extends FilterComponentType> =
  T extends "multiSelect"
    ? Pick<MultiSelectFilterData, "filterKey" | "label" | "items">
    : T extends "entity"
      ? Pick<EntityFilterProps, "filterKey" | "entityType">
      : T extends "dateRange"
        ? Pick<DateRangeFilterProps, "filterKey">
        : T extends "location"
          ? Pick<LocationFilterProps, "filterKey">
          : T extends "size"
            ? Pick<SizeFilterProps, "onApply">
            : T extends "performance"
              ? Pick<PerformanceFilterProps, "onApply">
              : T extends "vintageYear"
                ? Pick<VintageYearFilterProps, "onApply">
                : T extends "yearFounded"
                  ? Pick<YearFoundedFilterProps, "onApply">
                  : T extends "tree"
                    ? Partial<
                        Pick<
                          MultiSelectTreeFilterData,
                          "filterKey" | "label" | "treeData" | "keyToLabel"
                        >
                      >
                    : never;

export interface FilterConfig<
  C extends FilterComponentType = FilterComponentType,
> {
  // Identity
  id: string;
  label: string;
  category: FilterCategory;

  // Store mapping
  storeKeys: string | string[]; // Can map to multiple store keys (e.g., dateStart/dateEnd)

  // UI component
  component: C;
  componentProps?: FilterComponentProps<C>;

  // GraphQL query builder - strongly typed to KnowledgeBaseFilter
  toGraphQL: (
    activeFilters: ActiveFilters
  ) => Partial<types.KnowledgeBaseFilter>;

  // GraphQL to ActiveFilters mapping (for restoration)
  fromGraphQL?: (
    gql: Partial<types.KnowledgeBaseFilter>
  ) => Partial<ActiveFilters>;

  // Display - strongly typed to ActiveFilters
  getDisplayLabel: (activeFilters: ActiveFilters) => string | null;
  getDisplayCount?: (activeFilters: ActiveFilters) => number;

  // Data loading (optional - for dynamic filters)
  query?: DocumentNode;
  queryVariables?: Record<string, any>;
  // dataBuilder receives query data and returns filter definition
  // Changed to allow null returns and use the external FilterDefinition type
  dataBuilder?: (data: unknown) => ExternalFilterDefinition | null;

  // Ordering
  order: number;
}

// ============================================================================
// Relative Date Helpers
// ============================================================================

const RELATIVE_DATE_MAP: Record<
  string,
  { relativeN: number; relativeUnit: types.RelativeDateUnit; roundToStart: boolean }
> = {
  "1w": { relativeN: 1, relativeUnit: types.RelativeDateUnit.W, roundToStart: false },
  "2w": { relativeN: 2, relativeUnit: types.RelativeDateUnit.W, roundToStart: false },
  "1m": { relativeN: 1, relativeUnit: types.RelativeDateUnit.M, roundToStart: false },
  mtd: { relativeN: 0, relativeUnit: types.RelativeDateUnit.M, roundToStart: true },
  "3m": { relativeN: 3, relativeUnit: types.RelativeDateUnit.M, roundToStart: false },
  ytd: { relativeN: 0, relativeUnit: types.RelativeDateUnit.Y, roundToStart: true },
  "1y": { relativeN: 1, relativeUnit: types.RelativeDateUnit.Y, roundToStart: false },
  "3y": { relativeN: 3, relativeUnit: types.RelativeDateUnit.Y, roundToStart: false },
  "5y": { relativeN: 5, relativeUnit: types.RelativeDateUnit.Y, roundToStart: false },
};

function findRelativeShorthand(
  relativeN: number,
  relativeUnit: types.RelativeDateUnit,
  roundToStart: boolean
): string | undefined {
  return Object.entries(RELATIVE_DATE_MAP).find(
    ([, v]) =>
      v.relativeN === relativeN &&
      v.relativeUnit === relativeUnit &&
      v.roundToStart === roundToStart
  )?.[0];
}

// ============================================================================
// Helper Functions
// ============================================================================

const getArrayLabel = (
  values: FilterValue[] | undefined,
  singular: string,
  plural?: string
): string | null => {
  if (!values?.length) return null;
  if (values.length === 1) return `${singular}: ${values[0].label}`;
  return `${plural || singular} (${values.length})`;
};

const getSimpleArrayLabel = (
  values: any[] | undefined,
  singular: string,
  plural?: string
): string | null => {
  if (!values?.length) return null;
  if (values.length === 1) return `${singular}: ${values[0]}`;
  return `${plural || singular} (${values.length})`;
};

const buildInvestmentFilter = (
  filterType: string,
  values: FilterValue[]
): types.InvestmentSearchFilters => ({
  [filterType]: {
    values: values.map(v => ({
      id: Number(v.id),
      label: v.label,
    })),
  },
});

const buildFirmFilter = (
  filterType: string,
  values: FilterValue[]
): types.FirmSearchFilters => ({
  [filterType]: {
    values: values.map(v => ({
      id: Number(v.id),
      label: v.label,
    })),
  },
});

const buildCompanyFilter = (
  filterType: string,
  values: FilterValue[]
): types.CompanySearchFilters => ({
  [filterType]: {
    values: values.map(v => ({
      id: Number(v.id),
      label: v.label,
    })),
  },
});

// ============================================================================
// Filter Registry
// ============================================================================

export const FILTER_REGISTRY: Record<string, FilterConfig> = {
  // ==========================================================================
  // KNOWLEDGE BASE FILTERS
  // ==========================================================================

  entityTypes: {
    id: "entityType",
    label: "Item Type",
    category: "Knowledge Base",
    storeKeys: "entityTypes",
    component: "multiSelect",
    componentProps: {
      filterKey: "entityTypes",
      label: "Item Type",
      items: [
        { id: "NOTE", label: "Notes" },
        { id: "DOCUMENT", label: "Documents" },
        { id: "ENGAGEMENT", label: "Engagements" },
      ],
    },
    order: 10,
    toGraphQL: filters => {
      if (!filters.entityTypes?.length) return {};
      return {
        entityTypes: filters.entityTypes.map(
          (t: FilterValue) => t.id as types.KnowledgeBaseEntityType
        ),
      };
    },
    fromGraphQL: gql => {
      if (!gql.entityTypes) return {};
      const ENTITY_TYPE_LABELS: Record<string, string> = {
        NOTE: "Notes",
        DOCUMENT: "Documents",
        ENGAGEMENT: "Engagements",
      };
      return {
        entityTypes: Array.isArray(gql.entityTypes)
          ? gql.entityTypes.map((id: string) => {
              const enumId = typeof id === "string" ? id.toUpperCase() : String(id);
              return { id: enumId, label: ENTITY_TYPE_LABELS[enumId] || enumId };
            })
          : [],
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.entityTypes, "Type", "Types"),
  },

  date: {
    id: "date",
    label: "Date",
    category: "Knowledge Base",
    storeKeys: ["dateStart", "dateEnd", "dateRelative"],
    component: "dateRange",
    componentProps: {
      filterKey: "date",
    },
    order: 20,
    toGraphQL: filters => {
      if (filters.dateRelative && RELATIVE_DATE_MAP[filters.dateRelative]) {
        return { date: RELATIVE_DATE_MAP[filters.dateRelative] };
      }
      if (!filters.dateStart && !filters.dateEnd) return {};
      const dateFilter: types.DateRangeFilter = {};
      if (filters.dateStart) dateFilter.min = new Date(filters.dateStart);
      if (filters.dateEnd) dateFilter.max = new Date(filters.dateEnd);
      return { date: dateFilter };
    },
    fromGraphQL: gql => {
      if (!gql.date) return {};
      if (gql.date.relativeUnit != null && gql.date.relativeN != null) {
        const shorthand = findRelativeShorthand(
          gql.date.relativeN,
          gql.date.relativeUnit,
          gql.date.roundToStart ?? false
        );
        if (shorthand) return { dateRelative: shorthand };
      }
      return {
        dateStart: gql.date.min
          ? new Date(gql.date.min).toISOString().slice(0, 10)
          : undefined,
        dateEnd: gql.date.max
          ? new Date(gql.date.max).toISOString().slice(0, 10)
          : undefined,
      };
    },
    getDisplayLabel: filters => {
      if (filters.dateRelative) {
        return `Date: ${(filters.dateRelative as string).toUpperCase()}`;
      }
      if (!filters.dateStart && !filters.dateEnd) return null;
      return `Date: ${filters.dateStart || "?"} - ${filters.dateEnd || "?"}`;
    },
  },

  modifyDate: {
    id: "modifyDate",
    label: "Modified Date",
    category: "Knowledge Base",
    storeKeys: ["modifyDateStart", "modifyDateEnd", "modifyDateRelative"],
    component: "dateRange",
    componentProps: {
      filterKey: "modifyDate",
    },
    order: 30,
    toGraphQL: filters => {
      if (
        filters.modifyDateRelative &&
        RELATIVE_DATE_MAP[filters.modifyDateRelative as string]
      ) {
        return {
          modifyDate:
            RELATIVE_DATE_MAP[filters.modifyDateRelative as string],
        };
      }
      if (!filters.modifyDateStart && !filters.modifyDateEnd) return {};
      const modifyDateFilter: types.DateRangeFilter = {};
      if (filters.modifyDateStart)
        modifyDateFilter.min = new Date(filters.modifyDateStart);
      if (filters.modifyDateEnd)
        modifyDateFilter.max = new Date(filters.modifyDateEnd);
      return { modifyDate: modifyDateFilter };
    },
    fromGraphQL: gql => {
      if (!gql.modifyDate) return {};
      if (
        gql.modifyDate.relativeUnit != null &&
        gql.modifyDate.relativeN != null
      ) {
        const shorthand = findRelativeShorthand(
          gql.modifyDate.relativeN,
          gql.modifyDate.relativeUnit,
          gql.modifyDate.roundToStart ?? false
        );
        if (shorthand) return { modifyDateRelative: shorthand };
      }
      return {
        modifyDateStart: gql.modifyDate.min
          ? new Date(gql.modifyDate.min).toISOString().slice(0, 10)
          : undefined,
        modifyDateEnd: gql.modifyDate.max
          ? new Date(gql.modifyDate.max).toISOString().slice(0, 10)
          : undefined,
      };
    },
    getDisplayLabel: filters => {
      if (filters.modifyDateRelative) {
        return `Modified: ${(filters.modifyDateRelative as string).toUpperCase()}`;
      }
      if (!filters.modifyDateStart && !filters.modifyDateEnd) return null;
      return `Modified: ${filters.modifyDateStart || "?"} - ${filters.modifyDateEnd || "?"}`;
    },
  },

  knowledgeBaseTagIds: {
    id: "knowledgeBaseTags",
    label: "Knowledge Base Tags",
    category: "Knowledge Base",
    storeKeys: "knowledgeBaseTagIds",
    component: "entity",
    componentProps: {
      filterKey: "knowledgeBaseTagIds",
      label: "Knowledge Base Tags",
      entityType: "knowledgeBaseTag",
    },
    order: 40,
    toGraphQL: filters => {
      if (!filters.knowledgeBaseTagIds?.length) return {};
      return {
        knowledgeBaseTagIds: filters.knowledgeBaseTagIds.map(
          (tag: FilterValue) => Number(tag.id)
        ),
      };
    },
    fromGraphQL: gql => {
      if (!gql.knowledgeBaseTagIds?.length) return {};
      return {
        knowledgeBaseTagIds: gql.knowledgeBaseTagIds.map((id: number) => ({
          id,
          label: String(id),
        })),
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.knowledgeBaseTagIds, "KB Tag", "KB Tags"),
  },

  // ==========================================================================
  // NOTE FILTERS
  // ==========================================================================

  noteCreateUsers: {
    id: "noteCreateUser",
    label: "Note Creator",
    category: "Note",
    storeKeys: "noteCreateUsers",
    component: "entity",
    componentProps: {
      filterKey: "noteCreateUsers",
      entityType: "user",
    },
    order: 100,
    toGraphQL: filters => {
      if (!filters.noteCreateUsers?.length) return {};
      return {
        notesFilter: {
          createUsers: filters.noteCreateUsers.map((u: FilterValue) =>
            String(u.id)
          ),
        },
      };
    },
    fromGraphQL: gql => {
      if (!gql.notesFilter || !gql.notesFilter.createUsers) return {};
      return {
        noteCreateUsers: Array.isArray(gql.notesFilter.createUsers)
          ? gql.notesFilter.createUsers.map((id: string) => ({ id, label: id }))
          : [],
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.noteCreateUsers, "Note Creator", "Note Creators"),
  },

  noteMetaIds: {
    id: "noteMeta",
    label: "Note Meta",
    category: "Note",
    storeKeys: "noteMetaIds",
    component: "tree",
    order: 110,
    query: NOTE_META_ENUM_QUERY,
    dataBuilder: buildNoteMetaFilter,
    toGraphQL: filters => {
      if (!filters.noteMetaIds?.length) return {};

      // Extract note meta IDs from tree keys (format: "note-meta-123")
      const metaIds = filters.noteMetaIds
        .map((item: FilterValue) => {
          const key = String(item.id);
          if (key.startsWith("note-meta-")) {
            return Number(key.replace("note-meta-", ""));
          }
          return null;
        })
        .filter((id: number | null): id is number => id !== null);

      if (metaIds.length === 0) return {};

      return {
        notesFilter: {
          noteMetaIds: metaIds,
        },
      };
    },
    fromGraphQL: gql => {
      if (!gql.notesFilter || !gql.notesFilter.noteMetaIds) return {};
      return {
        noteMetaIds: Array.isArray(gql.notesFilter.noteMetaIds)
          ? gql.notesFilter.noteMetaIds.map((id: number) => ({
              id: `note-meta-${id}`,
              label: String(id),
            }))
          : [],
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.noteMetaIds, "Note Meta", "Note Metas"),
  },

  // ==========================================================================
  // DOCUMENT FILTERS
  // ==========================================================================

  documentCategories: {
    id: "documentType",
    label: "Document Type",
    category: "Document",
    storeKeys: "documentCategories",
    component: "tree",
    order: 200,
    query: DOCUMENT_TYPE_ENUM_QUERY,
    dataBuilder: buildDocumentTypeFilter,
    toGraphQL: filters => {
      const selected = filters.documentCategories || [];
      if (!selected.length) return {};

      // Extract IDs from tree keys (e.g., "doc-type-123")
      const typeIds = selected
        .map((item: FilterValue) => {
          const match = String(item.id).match(/^doc-type-(\d+)$/);
          return match ? Number(match[1]) : null;
        })
        .filter((id): id is number => id !== null);

      if (!typeIds.length) return {};
      return { documentsFilter: { documentTypeEnumIds: typeIds } };
    },
    fromGraphQL: gql => {
      if (!gql.documentsFilter || !gql.documentsFilter.documentTypeEnumIds)
        return {};
      return {
        documentCategories: Array.isArray(
          gql.documentsFilter.documentTypeEnumIds
        )
          ? gql.documentsFilter.documentTypeEnumIds.map((id: number) => ({
              id: `doc-type-${id}`,
              label: String(id),
            }))
          : [],
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(
        filters.documentCategories,
        "Doc Category",
        "Doc Categories"
      ),
  },

  documentFormats: {
    id: "documentFormat",
    label: "Document Format",
    category: "Document",
    storeKeys: "documentFormats",
    component: "multiSelect",
    order: 210,
    dataBuilder: buildDocumentFormatFilter,
    toGraphQL: filters => {
      if (!filters.documentFormats?.length) return {};
      return {
        documentsFilter: {
          extensions: filters.documentFormats.map(
            (f: FilterValue) => f.id as string
          ),
        },
      };
    },
    fromGraphQL: gql => {
      if (!gql.documentsFilter || !gql.documentsFilter.extensions) return {};
      return {
        documentFormats: Array.isArray(gql.documentsFilter.extensions)
          ? gql.documentsFilter.extensions.map((id: string) => ({
              id,
              label: id,
            }))
          : [],
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.documentFormats, "Doc Format", "Doc Formats"),
  },

  // ==========================================================================
  // INVESTMENT FILTERS
  // ==========================================================================

  investmentIds: {
    id: "investmentName",
    label: "Investment Name",
    category: "Investment",
    storeKeys: "investmentIds",
    component: "entity",
    componentProps: {
      filterKey: "investmentIds",
      entityType: "investment",
    },
    order: 300,
    toGraphQL: filters => {
      if (!filters.investmentIds?.length) return {};
      return {
        investmentFilters: [
          buildInvestmentFilter("INVESTMENT_ENTITY", filters.investmentIds),
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.investmentFilters) return {};
      const invFilter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(f => f.INVESTMENT_ENTITY)
        : undefined;
      if (
        !invFilter ||
        !invFilter.INVESTMENT_ENTITY ||
        !invFilter.INVESTMENT_ENTITY.values
      )
        return {};
      return {
        investmentIds: invFilter.INVESTMENT_ENTITY.values.map(
          (v: { id: number; label: string }) => ({ id: v.id, label: v.label })
        ),
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.investmentIds, "Investment", "Investments"),
  },

  investmentFirmIds: {
    id: "investmentFirmIds",
    label: "Investment's Firm",
    category: "Investment",
    storeKeys: "investmentFirmIds",
    component: "entity",
    componentProps: {
      filterKey: "investmentFirmIds",
      entityType: "firm",
    },
    order: 305,
    toGraphQL: filters => {
      const values = filters.investmentFirmIds as FilterValue[] | undefined;
      if (!values?.length) return {};
      return {
        investmentFilters: [buildInvestmentFilter("FIRM_ENTITY", values)],
      };
    },
    fromGraphQL: gql => {
      if (!gql.investmentFilters) return {};
      const firmFilter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(f => f.FIRM_ENTITY)
        : undefined;
      if (
        !firmFilter ||
        !firmFilter.FIRM_ENTITY ||
        !firmFilter.FIRM_ENTITY.values
      )
        return {};
      return {
        investmentFirmIds: firmFilter.FIRM_ENTITY.values.map(
          (v: { id: number; label: string }) => ({ id: v.id, label: v.label })
        ),
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(
        filters.investmentFirmIds as FilterValue[] | undefined,
        "Inv Firm",
        "Inv Firms"
      ),
  },

  assetClasses: {
    id: "assetClass",
    label: "Asset Class",
    category: "Investment",
    storeKeys: "assetClasses",
    component: "tree",
    order: 310,
    query: ASSET_CLASS_QUERY,
    dataBuilder: buildAssetClassFilter,
    toGraphQL: filters => {
      if (!filters.assetClasses?.length) return {};

      // Parse tree keys (format: "assetClassId" or "assetClassId^strategyId" or "assetClassId^strategyId^subStrategyId")
      // Group by asset class and build nested structure
      const assetClassMap = new Map<number, types.AssetClassStrategy>();

      // Track labels per ID for EntityKey construction
      const labelById = new Map<string, string>();
      filters.assetClasses.forEach((item: FilterValue) => {
        labelById.set(String(item.id), item.label);
      });

      filters.assetClasses.forEach((item: FilterValue) => {
        const key = String(item.id);
        const parts = key.split("^").map(Number);
        const [assetClassId, strategyId, subStrategyId] = parts;

        if (!assetClassId) return;

        // Get or create asset class entry
        let assetClassStrategy = assetClassMap.get(assetClassId);
        if (!assetClassStrategy) {
          // Use label from the asset-class-only key if available, otherwise from this item
          const acLabel = labelById.get(`${assetClassId}`) || item.label || "";
          assetClassStrategy = {
            assetClass: [{ id: assetClassId, label: acLabel }],
          };
          assetClassMap.set(assetClassId, assetClassStrategy);
        }

        // Add strategy or sub-strategy if present
        if (subStrategyId) {
          // Sub-strategy specified
          assetClassStrategy.subStrategy = assetClassStrategy.subStrategy || [];
          if (
            !assetClassStrategy.subStrategy.some(
              (s: types.EntityKey) => s.id === subStrategyId
            )
          ) {
            assetClassStrategy.subStrategy.push({
              id: subStrategyId,
              label: item.label || "",
            });
          }
        }
        if (strategyId) {
          // Strategy specified (with or without sub-strategy)
          assetClassStrategy.strategy = assetClassStrategy.strategy || [];
          if (
            !assetClassStrategy.strategy.some(
              (s: types.EntityKey) => s.id === strategyId
            )
          ) {
            // Use label from strategy-only key if available
            const stratLabel = labelById.get(`${assetClassId}^${strategyId}`) || item.label || "";
            assetClassStrategy.strategy.push({ id: strategyId, label: stratLabel });
          }
        }
        // If only asset class, nothing else to add
      });

      const values = Array.from(assetClassMap.values());

      return {
        investmentFilters: [
          {
            ASSET_CLASS_STRATEGY: {
              values,
            },
          },
        ],
      };
    },
    fromGraphQL: gql => {
      // Reconstruct tree keys from the GraphQL filter structure, preserving labels
      if (!gql.investmentFilters) return {};
      const acsFilter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(f => f.ASSET_CLASS_STRATEGY)
        : undefined;
      if (
        !acsFilter ||
        !acsFilter.ASSET_CLASS_STRATEGY ||
        !acsFilter.ASSET_CLASS_STRATEGY.values
      )
        return {};

      const keys: { id: string; label: string }[] = [];

      acsFilter.ASSET_CLASS_STRATEGY.values.forEach((v: any) => {
        if (v.assetClass?.[0]?.id) {
          const assetClassId = v.assetClass[0].id;
          const assetClassName = v.assetClass[0].label || "";

          if (v.subStrategy?.length) {
            if (v.strategy?.length) {
              v.strategy.forEach((strategy: any) => {
                v.subStrategy.forEach((subStrategy: any) => {
                  keys.push({
                    id: `${assetClassId}^${strategy.id}^${subStrategy.id}`,
                    label: subStrategy.label || strategy.label || assetClassName,
                  });
                });
              });
            } else {
              v.subStrategy.forEach((subStrategy: any) => {
                keys.push({
                  id: `${assetClassId}^^${subStrategy.id}`,
                  label: subStrategy.label || assetClassName,
                });
              });
            }
          } else if (v.strategy?.length) {
            v.strategy.forEach((strategy: any) => {
              keys.push({
                id: `${assetClassId}^${strategy.id}`,
                label: strategy.label || assetClassName,
              });
            });
          } else {
            keys.push({
              id: `${assetClassId}`,
              label: assetClassName,
            });
          }
        }
      });

      // Remove duplicates while preserving order
      const seen = new Set();
      const uniqueKeys = keys.filter(key => {
        if (seen.has(key.id)) {
          return false;
        }
        seen.add(key.id);
        return true;
      });

      return { assetClasses: uniqueKeys };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.assetClasses, "Asset Class", "Asset Classes"),
  },

  themes: {
    id: "theme",
    label: "Theme",
    category: "Investment",
    storeKeys: "themes",
    component: "tree",
    order: 320,
    query: THEME_OPTIONS_QUERY,
    dataBuilder: buildThemeFilter,
    toGraphQL: filters => {
      if (!filters.themes?.length) return {};

      // Extract impact enum IDs from tree keys (format: "theme-impactId")
      const impactIds: types.EntityKey[] = [];
      filters.themes.forEach((item: FilterValue) => {
        const key = String(item.id);
        const parts = key.split("-");

        if (parts.length === 2 && parts[0] === "theme") {
          // This is an impact enum (theme-impactId)
          const impactId = Number(parts[1]);
          if (impactId) {
            impactIds.push({ id: impactId, label: item.label });
          }
        }
        // If parts[1] === "cat" (theme-cat-categoryId), it's a category - backend doesn't support filtering by category alone
      });

      if (impactIds.length === 0) return {};

      return {
        investmentFilters: [
          {
            IMPACT: {
              values: impactIds,
              isOr: true, // Match any selected theme
            },
          },
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.investmentFilters) return {};
      const impactFilter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(f => f.IMPACT)
        : undefined;
      if (!impactFilter || !impactFilter.IMPACT || !impactFilter.IMPACT.values)
        return {};
      // Reconstruct tree keys: theme-impactId (impact IDs are unique across categories)
      return {
        themes: impactFilter.IMPACT.values.map(
          (v: { id: number; label: string }) => ({
            id: `theme-${v.id}`,
            label: v.label,
          })
        ),
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.themes, "Theme", "Themes"),
  },

  geographies: {
    id: "geography",
    label: "Geography",
    category: "Investment",
    storeKeys: "geographies",
    component: "tree",
    order: 330,
    query: GEOGRAPHY_OPTIONS_QUERY,
    dataBuilder: buildGeographyFilter,
    toGraphQL: filters => {
      if (!filters.geographies?.length) return {};
      return {
        investmentFilters: [
          buildInvestmentFilter("PRIMARY_GEOGRAPHY", filters.geographies),
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.investmentFilters) return {};
      const geoFilter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(f => f.PRIMARY_GEOGRAPHY)
        : undefined;
      if (
        !geoFilter ||
        !geoFilter.PRIMARY_GEOGRAPHY ||
        !geoFilter.PRIMARY_GEOGRAPHY.values
      )
        return {};
      // Reconstruct tree keys as id: value.id, label: value.label
      return {
        geographies: geoFilter.PRIMARY_GEOGRAPHY.values.map(
          (v: { id: number; label: string }) => ({ id: v.id, label: v.label })
        ),
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.geographies, "Geography", "Geographies"),
  },

  investmentTypes: {
    id: "investmentType",
    label: "Investment Type",
    category: "Investment",
    storeKeys: "investmentTypes",
    component: "multiSelect",
    order: 340,
    query: INVESTMENT_TYPE_OPTIONS_QUERY,
    dataBuilder: buildInvestmentTypeFilter,
    toGraphQL: filters => {
      if (!filters.investmentTypes?.length) return {};
      return {
        investmentFilters: [
          buildInvestmentFilter("TYPE", filters.investmentTypes),
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.investmentFilters) return {};
      const typeFilter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(f => f.TYPE)
        : undefined;
      if (!typeFilter || !typeFilter.TYPE || !typeFilter.TYPE.values) return {};
      return {
        investmentTypes: typeFilter.TYPE.values.map(
          (v: { id: number; label: string }) => ({ id: v.id, label: v.label })
        ),
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.investmentTypes, "Inv Type", "Inv Types"),
  },

  investmentTags: {
    id: "investmentTags",
    label: "Investment Tags",
    category: "Investment",
    storeKeys: "investmentTags",
    component: "tree",
    order: 350,
    query: INVESTMENT_TAGS_OPTIONS_QUERY,
    queryVariables: { filter: { investmentCountGTE: 1 } },
    dataBuilder: buildInvestmentTagsFilter,
    toGraphQL: filters => {
      if (!filters.investmentTags?.length) return {};
      // Extract numeric IDs from tree keys (e.g., "tag-123" -> 123)
      const tagValues = filters.investmentTags
        .map((v: FilterValue) => {
          const match = String(v.id).match(/^tag-(\d+)$/);
          if (!match) return null;
          return { id: Number(match[1]), label: v.label };
        })
        .filter(
          (
            v: { id: number; label: string } | null
          ): v is { id: number; label: string } => v !== null
        );

      if (!tagValues.length) return {};
      return {
        investmentFilters: [{ TAG_ENTITY: { values: tagValues } }],
      };
    },
    fromGraphQL: gql => {
      if (!gql.investmentFilters) return {};
      const tagFilter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(f => f.TAG_ENTITY)
        : undefined;
      if (!tagFilter || !tagFilter.TAG_ENTITY || !tagFilter.TAG_ENTITY.values)
        return {};
      return {
        investmentTags: tagFilter.TAG_ENTITY.values.map(
          (v: { id: number; label: string }) => ({
            id: `tag-${v.id}`,
            label: v.label,
          })
        ),
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.investmentTags, "Inv Tag", "Inv Tags"),
  },

  investmentListIds: {
    id: "investmentList",
    label: "Investment List",
    category: "Investment",
    storeKeys: "investmentListIds",
    component: "entity",
    componentProps: {
      filterKey: "investmentListIds",
      entityType: "investmentSet",
    },
    order: 360,
    toGraphQL: filters => {
      if (!filters.investmentListIds?.length) return {};
      // Only supports single selection
      return {
        investmentFilters: [
          {
            INVESTMENT_SET: {
              investmentSetId: Number(filters.investmentListIds[0].id),
            },
          },
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.investmentFilters) return {};
      const setFilter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(f => f.INVESTMENT_SET)
        : undefined;
      if (
        !setFilter ||
        !setFilter.INVESTMENT_SET ||
        !setFilter.INVESTMENT_SET.investmentSetId
      )
        return {};
      return {
        investmentListIds: [
          {
            id: setFilter.INVESTMENT_SET.investmentSetId,
            label: String(setFilter.INVESTMENT_SET.investmentSetId),
          },
        ],
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.investmentListIds, "Inv List", "Inv Lists"),
  },

  size: {
    id: "size",
    label: "Size",
    category: "Investment",
    storeKeys: "size",
    component: "size",
    componentProps: {
      onApply: () => {}, // Placeholder - will be overridden by actual component
    },
    order: 370,
    toGraphQL: filters => {
      if (!filters.size) return {};
      return {
        investmentFilters: [
          {
            SIZE: {
              min: filters.size.min,
              max: filters.size.max,
            },
          },
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.investmentFilters) return {};
      const sizeFilter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(f => f.SIZE)
        : undefined;
      if (!sizeFilter || !sizeFilter.SIZE) return {};
      // Only assign min/max if they are numbers (not null/undefined)
      const min =
        typeof sizeFilter.SIZE.min === "number"
          ? sizeFilter.SIZE.min
          : undefined;
      const max =
        typeof sizeFilter.SIZE.max === "number"
          ? sizeFilter.SIZE.max
          : undefined;
      return {
        size: {
          min,
          max,
        },
      };
    },
    getDisplayLabel: filters => {
      if (!filters.size) return null;
      const { min, max } = filters.size;
      const minLabel = min ? `$${Math.round(min / 1e6)}M` : "?";
      const maxLabel = max ? `$${Math.round(max / 1e6)}M` : "?";
      return `Size: ${minLabel} - ${maxLabel}`;
    },
  },

  performance: {
    id: "performance",
    label: "Performance",
    category: "Investment",
    storeKeys: "performance",
    component: "performance",
    order: 375,
    toGraphQL: filters => {
      const performance = filters.performance;
      if (!performance) return {};
      const range = { min: performance.min, max: performance.max };
      return {
        investmentFilters: [
          performance.type === "PERFORMANCE"
            ? { PERFORMANCE: { statType: performance.statType, ...range } }
            : {
                PERFORMANCE_PRIVATE: {
                  statType: performance.statType,
                  ...range,
                },
              },
        ],
      };
    },
    fromGraphQL: gql => {
      const filter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(
            f => f.PERFORMANCE || f.PERFORMANCE_PRIVATE
          )
        : undefined;
      const range = ({ min, max }: types.FloatRangeFilter) => ({
        min: min ?? undefined,
        max: max ?? undefined,
      });
      if (filter?.PERFORMANCE) {
        const { statType } = filter.PERFORMANCE;
        return {
          performance: {
            type: "PERFORMANCE",
            statType,
            ...range(filter.PERFORMANCE),
          },
        };
      }
      if (filter?.PERFORMANCE_PRIVATE) {
        const { statType } = filter.PERFORMANCE_PRIVATE;
        return {
          performance: {
            type: "PERFORMANCE_PRIVATE",
            statType,
            ...range(filter.PERFORMANCE_PRIVATE),
          },
        };
      }
      return {};
    },
    getDisplayLabel: filters => {
      if (!filters.performance) return null;
      const { min, max, statType } = filters.performance;
      const { scale, suffix } = getPerformanceUnit(filters.performance);
      const format = (value?: number) =>
        value === undefined ? "?" : `${round(value * scale, 2)}${suffix}`;
      return `Performance (${getPerformanceStatLabel(statType)}): ${format(min)} - ${format(max)}`;
    },
  },

  headquarters: {
    id: "headquarters",
    label: "Headquarters",
    category: "Investment",
    storeKeys: "headquarters",
    component: "location",
    componentProps: {
      filterKey: "headquarters",
      label: "Headquarters",
    },
    order: 380,
    toGraphQL: filters => {
      if (!filters.headquarters?.length) return {};
      const [loc] = filters.headquarters;
      const [countryId, stateId, city] = String(loc.id).split("-");
      return {
        firmFilters: [
          {
            HEADQUARTERS: {
              countryEnum: countryId ? Number(countryId) : undefined,
              stateEnum: stateId !== "null" ? Number(stateId) : undefined,
              city: city !== "null" ? city : undefined,
            },
          },
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.firmFilters) return {};
      const hqFilter = Array.isArray(gql.firmFilters)
        ? gql.firmFilters.find(f => f.HEADQUARTERS)
        : undefined;
      if (!hqFilter || !hqFilter.HEADQUARTERS) return {};
      const { countryEnum, stateEnum, city } = hqFilter.HEADQUARTERS;
      // Compose id as in toGraphQL
      const id = [
        countryEnum ?? "null",
        stateEnum ?? "null",
        city ?? "null",
      ].join("-");
      return {
        headquarters: [{ id, label: id }],
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.headquarters, "HQ", "HQ Locations"),
  },

  vintageYears: {
    id: "vintageYear",
    label: "Vintage Year",
    category: "Investment",
    storeKeys: "vintageYears",
    component: "vintageYear",
    componentProps: {
      filterKey: "vintageYear",
      label: "Vintage Year",
    },
    order: 390,
    toGraphQL: filters => {
      if (!filters.vintageYears?.length) return {};
      const min = Math.min(...filters.vintageYears);
      const max = Math.max(...filters.vintageYears);
      return {
        investmentFilters: [
          {
            VINTAGE_YEAR: { min, max },
          },
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.investmentFilters) return {};
      const vintageFilter = Array.isArray(gql.investmentFilters)
        ? gql.investmentFilters.find(f => f.VINTAGE_YEAR)
        : undefined;
      if (!vintageFilter || !vintageFilter.VINTAGE_YEAR) return {};
      const { min, max } = vintageFilter.VINTAGE_YEAR;
      if (min == null && max == null) return {};
      // Only include numbers, filter out null/undefined
      if (min === max && typeof min === "number")
        return { vintageYears: [min] };
      const years: number[] = [];
      if (typeof min === "number") years.push(min);
      if (typeof max === "number" && max !== min) years.push(max);
      return { vintageYears: years };
    },
    getDisplayLabel: filters => {
      if (!filters.vintageYears?.length) return null;
      const min = Math.min(...filters.vintageYears);
      const max = Math.max(...filters.vintageYears);
      return min === max ? `Vintage: ${min}` : `Vintage: ${min} - ${max}`;
    },
  },

  // ==========================================================================
  // FIRM FILTERS
  // ==========================================================================

  firmIds: {
    id: "firmIds",
    label: "Firm Name",
    category: "Firm",
    storeKeys: "firmIds",
    component: "entity",
    componentProps: {
      filterKey: "firmIds",
      label: "Firm Name",
      entityType: "firm",
    },
    order: 1100,
    toGraphQL: filters => {
      if (!filters.firmIds?.length) return {};
      return {
        firmFilters: [buildFirmFilter("FIRM_ENTITY", filters.firmIds)],
      };
    },
    fromGraphQL: gql => {
      if (!gql.firmFilters) return {};
      const firmFilter = Array.isArray(gql.firmFilters)
        ? gql.firmFilters.find(f => f.FIRM_ENTITY)
        : undefined;
      if (
        !firmFilter ||
        !firmFilter.FIRM_ENTITY ||
        !firmFilter.FIRM_ENTITY.values
      )
        return {};
      return {
        firmIds: firmFilter.FIRM_ENTITY.values.map(
          (v: { id: number; label: string }) => ({ id: v.id, label: v.label })
        ),
      };
    },
    getDisplayLabel: filters => getArrayLabel(filters.firmIds, "Firm", "Firms"),
  },

  // ==========================================================================
  // COMPANY FILTERS
  // ==========================================================================

  companyIds: {
    id: "companyIds",
    label: "Company Name",
    category: "Company",
    storeKeys: "companyIds",
    component: "entity",
    componentProps: {
      filterKey: "companyIds",
      label: "Company Name",
      entityType: "company",
    },
    order: 500,
    toGraphQL: filters => {
      if (!filters.companyIds?.length) return {};
      return {
        companyFilters: [
          buildCompanyFilter("COMPANY_ENTITY", filters.companyIds),
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.companyFilters) return {};
      const companyFilter = Array.isArray(gql.companyFilters)
        ? gql.companyFilters.find(f => f.COMPANY_ENTITY)
        : undefined;
      if (
        !companyFilter ||
        !companyFilter.COMPANY_ENTITY ||
        !companyFilter.COMPANY_ENTITY.values
      )
        return {};
      return {
        companyIds: companyFilter.COMPANY_ENTITY.values.map(
          (v: { id: number; label: string }) => ({ id: v.id, label: v.label })
        ),
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.companyIds, "Company", "Companies"),
  },

  companyIndustries: {
    id: "industry",
    label: "Industry",
    category: "Company",
    storeKeys: "companyIndustries",
    component: "tree",
    order: 510,
    query: INDUSTRY_OPTIONS_QUERY,
    queryVariables: { filter: { tagClassIds: [22] } },
    dataBuilder: buildIndustryFilter,
    toGraphQL: filters => {
      if (!filters.companyIndustries?.length) return {};
      return {
        companyFilters: [
          buildCompanyFilter("TAG_ENTITY", filters.companyIndustries),
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.companyFilters) return {};
      const tagFilter = Array.isArray(gql.companyFilters)
        ? gql.companyFilters.find(f => f.TAG_ENTITY)
        : undefined;
      if (!tagFilter || !tagFilter.TAG_ENTITY || !tagFilter.TAG_ENTITY.values)
        return {};
      return {
        companyIndustries: tagFilter.TAG_ENTITY.values.map(
          (v: { id: number; label: string }) => ({ id: v.id, label: v.label })
        ),
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(filters.companyIndustries, "Industry", "Industries"),
  },

  companyHeadquarters: {
    id: "companyHeadquarters",
    label: "Company Headquarters",
    category: "Company",
    storeKeys: "companyHeadquarters",
    component: "location",
    componentProps: {
      filterKey: "companyHeadquarters",
      label: "Company Headquarters",
    },
    order: 520,
    toGraphQL: filters => {
      if (!filters.companyHeadquarters?.length) return {};
      const [loc] = filters.companyHeadquarters;
      const [countryId, stateId, city] = String(loc.id).split("-");
      return {
        companyFilters: [
          {
            HEADQUARTERS: {
              countryEnum: countryId ? Number(countryId) : undefined,
              stateEnum: stateId !== "null" ? Number(stateId) : undefined,
              city: city !== "null" ? city : undefined,
            },
          },
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.companyFilters) return {};
      const hqFilter = Array.isArray(gql.companyFilters)
        ? gql.companyFilters.find(f => f.HEADQUARTERS)
        : undefined;
      if (!hqFilter || !hqFilter.HEADQUARTERS) return {};
      const { countryEnum, stateEnum, city } = hqFilter.HEADQUARTERS;
      const id = [
        countryEnum ?? "null",
        stateEnum ?? "null",
        city ?? "null",
      ].join("-");
      return {
        companyHeadquarters: [{ id, label: id }],
      };
    },
    getDisplayLabel: filters =>
      getArrayLabel(
        filters.companyHeadquarters,
        "Company HQ",
        "Company HQ Locations"
      ),
  },

  yearsFound: {
    id: "yearFounded",
    label: "Year Founded",
    category: "Company",
    storeKeys: "yearsFound",
    component: "yearFounded",
    componentProps: {
      filterKey: "yearFounded",
      label: "Year Founded",
    },
    order: 530,
    toGraphQL: filters => {
      if (!filters.yearsFound?.length) return {};
      const min = Math.min(...filters.yearsFound);
      const max = Math.max(...filters.yearsFound);
      return {
        companyFilters: [
          {
            YEAR_FOUNDED: { min, max },
          },
        ],
      };
    },
    fromGraphQL: gql => {
      if (!gql.companyFilters) return {};
      const yearFilter = Array.isArray(gql.companyFilters)
        ? gql.companyFilters.find(f => f.YEAR_FOUNDED)
        : undefined;
      if (!yearFilter || !yearFilter.YEAR_FOUNDED) return {};
      const { min, max } = yearFilter.YEAR_FOUNDED;
      if (min == null && max == null) return {};
      // Only include numbers, filter out null/undefined
      if (min === max && typeof min === "number") return { yearsFound: [min] };
      const years: number[] = [];
      if (typeof min === "number") years.push(min);
      if (typeof max === "number" && max !== min) years.push(max);
      return { yearsFound: years };
    },
    getDisplayLabel: filters => {
      if (!filters.yearsFound?.length) return null;
      const min = Math.min(...filters.yearsFound);
      const max = Math.max(...filters.yearsFound);
      return min === max ? `Founded: ${min}` : `Founded: ${min} - ${max}`;
    },
  },
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get all filters ordered by their order property
 */
export function getOrderedFilters(): FilterConfig[] {
  return Object.values(FILTER_REGISTRY).sort((a, b) => a.order - b.order);
}

/**
 * Get filters grouped by category
 */
export function getFiltersByCategory(): Record<FilterCategory, FilterConfig[]> {
  const grouped: Record<string, FilterConfig[]> = {};

  Object.values(FILTER_REGISTRY).forEach(config => {
    if (!grouped[config.category]) {
      grouped[config.category] = [];
    }
    grouped[config.category].push(config);
  });

  // Sort within each category
  Object.keys(grouped).forEach(category => {
    grouped[category].sort((a, b) => a.order - b.order);
  });

  return grouped as Record<FilterCategory, FilterConfig[]>;
}

/**
 * Get filter config by store key
 */
export function getFilterByStoreKey(
  storeKey: string
): FilterConfig | undefined {
  return Object.values(FILTER_REGISTRY).find(config => {
    if (Array.isArray(config.storeKeys)) {
      return config.storeKeys.includes(storeKey);
    }
    return config.storeKeys === storeKey;
  });
}

/**
 * Get filter config by filter ID
 */
export function getFilterById(id: string): FilterConfig | undefined {
  return Object.values(FILTER_REGISTRY).find(config => config.id === id);
}

/**
 * Get category color
 */
export function getCategoryColor(category: FilterCategory): string {
  const colorMap: Record<FilterCategory, string> = {
    "Knowledge Base": ENTITY_COLORS.knowledgeBase,
    Note: ENTITY_COLORS.note,
    Document: ENTITY_COLORS.document,
    Investment: ENTITY_COLORS.investment,
    Firm: ENTITY_COLORS.firm,
    Company: ENTITY_COLORS.company,
  };
  return colorMap[category];
}
